import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Group, Loader, Text } from "@mantine/core";
import { Button } from "@xplatform/design-system";
import { IconSitemap, IconAlertTriangle, IconRefresh } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { useMappingJob } from "@hooks/extraction/useMappingJob";
import { intelligenceEngineApi } from "@services/extractionApi";
import { mappingApi } from "@services/mappingApi";
import { MappingViewStatus } from "@models/mapping";

interface OpenMappingControlProps {
  processId: string;
  enabled?: boolean;
  /**
   * Offer "Retry mapping" when mapping failed or never started. A batch shows
   * the action once, in its process header, so its per-item views pass false.
   */
  showRetry?: boolean;
}

/**
 * Sits to the left of the Export button in the extraction-result header.
 *
 * During the async-create gap (extraction done, mapping job not yet created)
 * it shows a "starting…" hint; while the job runs it shows a progress label;
 * on failure (including when no job appears) an error label; once `Completed`
 * ("ready for view") it swaps to an "Open mapping" button that deep-links to
 * the job's result editor inside the mapping module of the shell.
 *
 * On failure — a Failed job, or no job within the start window — it offers
 * "Retry mapping", which re-dispatches the process and returns every status
 * view of it to "starting…" until the new job appears.
 *
 * Renders nothing when disabled.
 */
const OpenMappingControl: React.FC<OpenMappingControlProps> = ({
  processId,
  enabled = true,
  showRetry = true,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { job, waiting } = useMappingJob(processId, enabled);
  const [retrying, setRetrying] = useState(false);
  const [retryFailed, setRetryFailed] = useState(false);

  const handleRetry = async (event: React.MouseEvent) => {
    event.stopPropagation();
    if (retrying) return;
    setRetrying(true);
    setRetryFailed(false);
    try {
      await intelligenceEngineApi.retryMapping(processId);
      mappingApi.markRetried(processId, job?.jobId ?? null);
    } catch (error) {
      console.error("Failed to retry mapping:", error);
      setRetryFailed(true);
    } finally {
      setRetrying(false);
    }
  };

  const failedState = (
    <Group gap={8} wrap="nowrap">
      <Group gap={4} wrap="nowrap" c="red">
        <IconAlertTriangle size={14} />
        <Text size="xs">
          {retryFailed
            ? t("extraction.mappingStatus.retryFailed")
            : t("extraction.mappingStatus.failed")}
        </Text>
      </Group>
      {showRetry && (
        <Button
          size="xs"
          variant="light"
          color="brand"
          leftSection={<IconRefresh size={16} />}
          loading={retrying}
          onClick={handleRetry}
          className="retry-mapping-button"
        >
          {t("extraction.mappingStatus.retryMapping")}
        </Button>
      )}
    </Group>
  );

  if (!job) {
    if (!enabled || !processId) return null;
    if (!waiting) {
      return failedState;
    }
    return (
      <Group gap={6} wrap="nowrap">
        <Loader size={12} />
        <Text size="xs" c="dimmed">
          {t("extraction.mappingStatus.starting")}
        </Text>
      </Group>
    );
  }

  if (job.status === MappingViewStatus.Completed) {
    // serviceId is the `${kind}#${pluginId}` slug the mapping module routes on
    // (see mapping-frontend buildMappingServiceViewModel / parseServiceId).
    const serviceId = `${job.service ?? ""}#${job.pluginId ?? ""}`;
    const target = `/mapping/services/${encodeURIComponent(serviceId)}/jobs/${encodeURIComponent(job.jobId)}`;
    const canOpen = Boolean(job.jobId && job.service && job.pluginId);

    const button = (
      <Button
        size="xs"
        variant="filled"
        color="brand"
        leftSection={<IconSitemap size={16} />}
        disabled={!canOpen}
        onClick={() => navigate(target)}
        className="open-mapping-button"
      >
        {t("extraction.mappingStatus.openMapping")}
      </Button>
    );

    return job.failedFiles > 0 ? (
      <Group gap={8} wrap="nowrap">
        <Group gap={4} wrap="nowrap" c="orange">
          <IconAlertTriangle size={14} />
          <Text size="xs">{t("extraction.mappingStatus.partial", { count: job.failedFiles })}</Text>
        </Group>
        {button}
      </Group>
    ) : button;
  }

  if (job.status === MappingViewStatus.Failed) {
    return failedState;
  }

  // Running (AwaitingUpload / Parsing / Running).
  const hasTotal = job.rowsTotal > 0;
  const label = hasTotal
    ? t("extraction.mappingStatus.rows", {
        done: job.rowsDone,
        total: job.rowsTotal,
      })
    : t("extraction.mappingStatus.preparing");

  return (
    <Group gap={6} wrap="nowrap">
      <Loader size={12} />
      <Text size="xs" c="dimmed">
        {label}
      </Text>
    </Group>
  );
};

export default OpenMappingControl;
